import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiGraduationCap, PiPhone, PiEnvelopeSimple, PiMapPin, PiArrowRight } from "react-icons/pi";
import { getImageUrl } from "@/utils/image-url";

// Named export for index.tsx re-export
export function EducaeetFooter({ content, onContentChange, isSelected, defaultLogo, defaultSiteName }: SectionContentProps) {
    const fontFamily = content.fontFamily || "'Inter', 'Plus Jakarta Sans', sans-serif";

    const rawLogo = content.logo || defaultLogo || "Instacks";
    const activeLogo = (rawLogo.startsWith("/") || rawLogo.startsWith("http") || rawLogo.startsWith("data:image"))
        ? (getImageUrl(rawLogo) || rawLogo)
        : rawLogo;

    const usefulLinks = content.usefulLinks || [
        { label: "Marketplace", href: "#" },
        { label: "Kindergarten", href: "#" },
        { label: "University", href: "#" },
        { label: "GYM Coaching", href: "#" },
        { label: "Cooking", href: "#" }
    ];

    const companyLinks = content.companyLinks || [
        { label: "Contact Us", href: "#" },
        { label: "Become Teacher", href: "#" },
        { label: "Blog", href: "#" },
        { label: "Instructor", href: "#" },
        { label: "Events", href: "#" }
    ];

    const contact = {
        phone: content.contact?.phone || content.phone || "(+91) 123-456-789",
        email: content.contact?.email || content.email || "educaeet@gmail.com",
        location: content.contact?.location || content.contact?.address || content.address || content.location || "North America, USA"
    };

    const handleContactChange = (field: "phone" | "email" | "location", val: string) => {
        if (!onContentChange) return;
        const updatedContact = { ...contact, [field]: val };
        onContentChange({
            ...content,
            contact: updatedContact,
            ...(field === "phone" ? { phone: val } : {}),
            ...(field === "email" ? { email: val } : {}),
            ...(field === "location" ? { address: val, location: val } : {}),
        });
    };

    const socialLinks = {
        twitter: content.socialLinks?.twitter || content.twitter || "#",
        facebook: content.socialLinks?.facebook || content.facebook || "#",
        instagram: content.socialLinks?.instagram || content.instagram || "#",
        linkedin: content.socialLinks?.linkedin || content.linkedin || "#",
    };

    const taglineText = content.tagline || content.description || "Access expert-led courses designed to help you succeed in your career, all from the comfort of your home.";

    return (
        <footer
            className={`relative overflow-hidden ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ fontFamily, background: "linear-gradient(135deg, #e8f5f0 0%, #f0faf6 40%, #fafbf5 70%, #f5f8fa 100%)" }}
        >
            {/* Yellow Circle + Rotating Dotted Circle — Top Right */}
            <div className="absolute -top-10 -right-10 w-72 h-72 pointer-events-none z-0 hidden md:block">
                <style>{`
                    @keyframes rotateDots {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                    .rotate-dots-layer {
                        animation: rotateDots 30s linear infinite;
                        transform-origin: center center;
                    }
                `}</style>

                {/* Solid yellow filled circle — static */}
                <div
                    className="absolute"
                    style={{
                        width: "200px",
                        height: "200px",
                        borderRadius: "50%",
                        backgroundColor: "#f5d960",
                        top: "40px",
                        left: "20px",
                    }}
                />

                {/* Rotating dot-grid circle overlay */}
                <svg
                    width="220"
                    height="220"
                    viewBox="0 0 220 220"
                    className="absolute rotate-dots-layer"
                    style={{ top: "28px", left: "38px" }}
                >
                    {Array.from({ length: 13 }).map((_, row) =>
                        Array.from({ length: 13 }).map((_, col) => {
                            const cx = col * 16 + 8;
                            const cy = row * 16 + 8;
                            const dx = cx - 104;
                            const dy = cy - 104;
                            if (dx * dx + dy * dy > 105 * 105) return null;
                            return (
                                <circle
                                    key={`${row}-${col}`}
                                    cx={cx}
                                    cy={cy}
                                    r="4"
                                    fill="#a78bca"
                                    opacity="0.75"
                                />
                            );
                        })
                    )}
                </svg>
            </div>

            {/* Dot Grid — Left Side */}
            <div className="absolute left-4 top-1/2 -translate-y-1/2 opacity-25 pointer-events-none z-0 hidden md:block">
                <svg width="140" height="160" viewBox="0 0 140 160" fill="none">
                    {Array.from({ length: 6 }).map((_, row) =>
                        Array.from({ length: 5 }).map((_, col) => (
                            <circle
                                key={`${row}-${col}`}
                                cx={col * 26 + 13}
                                cy={row * 26 + 13}
                                r="4"
                                fill="#074d3d"
                            />
                        ))
                    )}
                </svg>
            </div>

            {/* Main Footer Content */}
            <div className="relative z-10 pt-14 pb-10">
                <div className="container mx-auto px-6 max-w-7xl">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-0 divide-y md:divide-y-0 md:divide-x divide-emerald-800/15">

                        {/* COLUMN 1 — Brand + Tagline + CTA */}
                        <div className="pb-8 md:pb-0 md:pr-10">
                            {/* Logo */}
                            <div className="flex items-center gap-2.5 mb-5">
                                {activeLogo && (activeLogo.startsWith("/") || activeLogo.startsWith("http") || activeLogo.startsWith("data:image")) ? (
                                    // Show uploaded logo image
                                    <img
                                        src={activeLogo}
                                        alt="Logo"
                                        className="h-10 w-auto object-contain"
                                    />
                                ) : (
                                    // Show icon + text brand name
                                    <>
                                        <div
                                            className="w-9 h-9 rounded-full flex items-center justify-center shadow-md shrink-0"
                                            style={{ backgroundColor: "#074d3d" }}
                                        >
                                            <PiGraduationCap className="w-5 h-5 text-white" />
                                        </div>
                                        <span className="text-xl font-bold tracking-tight" style={{ color: "#074d3d" }}>
                                            <EditableText
                                                value={activeLogo}
                                                onChange={(v) => onContentChange?.({ ...content, logo: v })}
                                            />
                                        </span>
                                    </>
                                )}
                            </div>

                            {/* Tagline / Description */}
                            <p className="text-gray-500 text-sm leading-relaxed mb-7 max-w-[230px]">
                                <EditableText
                                    value={taglineText}
                                    onChange={(v) => {
                                        onContentChange?.({ ...content, tagline: v, description: v });
                                    }}
                                />
                            </p>

                            {/* Contact Us CTA Button */}
                            <a
                                href={content.contactCtaLink || content.contactCtaUrl || "#contact"}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm text-white transition-all hover:opacity-90 hover:scale-105 shadow-md"
                                style={{ backgroundColor: "#074d3d" }}
                            >
                                <EditableText
                                    value={content.contactCtaText || "Contact Us"}
                                    onChange={(v) => onContentChange?.({ ...content, contactCtaText: v })}
                                />
                                <PiArrowRight className="w-4 h-4" />
                            </a>
                        </div>

                        {/* COLUMN 2 — Useful Links */}
                        <div className="pt-8 md:pt-0 md:px-10">
                            <h4 className="text-base font-bold text-gray-900 mb-5 tracking-tight">
                                <EditableText
                                    value={content.usefulLinksTitle || "Useful Links"}
                                    onChange={(v) => onContentChange?.({ ...content, usefulLinksTitle: v })}
                                />
                            </h4>
                            <ul className="space-y-3">
                                {usefulLinks.map((link: any, idx: number) => (
                                    <li key={idx}>
                                        <a
                                            href={link.href || "#"}
                                            className="text-gray-500 hover:text-emerald-800 text-sm transition-colors"
                                        >
                                            <EditableText
                                                value={link.label}
                                                onChange={(v) => {
                                                    const newLinks = [...usefulLinks];
                                                    newLinks[idx] = { ...newLinks[idx], label: v };
                                                    onContentChange?.({ ...content, usefulLinks: newLinks });
                                                }}
                                            />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* COLUMN 3 — Our Company */}
                        <div className="pt-8 md:pt-0 md:px-10">
                            <h4 className="text-base font-bold text-gray-900 mb-5 tracking-tight">
                                <EditableText
                                    value={content.companyLinksTitle || "Our Company"}
                                    onChange={(v) => onContentChange?.({ ...content, companyLinksTitle: v })}
                                />
                            </h4>
                            <ul className="space-y-3">
                                {companyLinks.map((link: any, idx: number) => (
                                    <li key={idx}>
                                        <a
                                            href={link.href || "#"}
                                            className="text-gray-500 hover:text-emerald-800 text-sm transition-colors"
                                        >
                                            <EditableText
                                                value={link.label}
                                                onChange={(v) => {
                                                    const newLinks = [...companyLinks];
                                                    newLinks[idx] = { ...newLinks[idx], label: v };
                                                    onContentChange?.({ ...content, companyLinks: newLinks });
                                                }}
                                            />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* COLUMN 4 — Get Contact */}
                        <div className="pt-8 md:pt-0 md:pl-10">
                            <h4 className="text-base font-bold text-gray-900 mb-5 tracking-tight">
                                <EditableText
                                    value={content.contactTitle || "Get Contact"}
                                    onChange={(v) => onContentChange?.({ ...content, contactTitle: v })}
                                />
                            </h4>

                            <ul className="space-y-3 mb-6">
                                <li className="flex items-start gap-2 text-sm text-gray-600">
                                    <span className="font-bold text-gray-900 shrink-0">Phone:</span>
                                    <a href={`tel:${contact.phone}`} className="hover:text-emerald-800 transition-colors">
                                        <EditableText
                                            value={contact.phone}
                                            onChange={(v) => handleContactChange("phone", v)}
                                        />
                                    </a>
                                </li>
                                <li className="flex items-start gap-2 text-sm text-gray-600">
                                    <span className="font-bold text-gray-900 shrink-0">Email:</span>
                                    <a href={`mailto:${contact.email}`} className="hover:text-emerald-800 transition-colors">
                                        <EditableText
                                            value={contact.email}
                                            onChange={(v) => handleContactChange("email", v)}
                                        />
                                    </a>
                                </li>
                                <li className="flex items-start gap-2 text-sm text-gray-600">
                                    <span className="font-bold text-gray-900 shrink-0">Location:</span>
                                    <span>
                                        <EditableText
                                            value={contact.location}
                                            onChange={(v) => handleContactChange("location", v)}
                                        />
                                    </span>
                                </li>
                            </ul>

                            {/* Social Icons Row — X, Facebook, Instagram, LinkedIn */}
                            <div className="flex items-center gap-3">
                                {/* X / Twitter */}
                                <a
                                    href={socialLinks.twitter}
                                    target={socialLinks.twitter !== "#" ? "_blank" : undefined}
                                    rel="noopener noreferrer"
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-emerald-800/10 transition-all"
                                    aria-label="Twitter"
                                >
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                    </svg>
                                </a>
                                {/* Facebook */}
                                <a
                                    href={socialLinks.facebook}
                                    target={socialLinks.facebook !== "#" ? "_blank" : undefined}
                                    rel="noopener noreferrer"
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-emerald-800/10 transition-all"
                                    aria-label="Facebook"
                                >
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                    </svg>
                                </a>
                                {/* Instagram */}
                                <a
                                    href={socialLinks.instagram}
                                    target={socialLinks.instagram !== "#" ? "_blank" : undefined}
                                    rel="noopener noreferrer"
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-emerald-800/10 transition-all"
                                    aria-label="Instagram"
                                >
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                    </svg>
                                </a>
                                {/* LinkedIn */}
                                <a
                                    href={socialLinks.linkedin}
                                    target={socialLinks.linkedin !== "#" ? "_blank" : undefined}
                                    rel="noopener noreferrer"
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-emerald-800/10 transition-all"
                                    aria-label="LinkedIn"
                                >
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                    </svg>
                                </a>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            {/* Copyright Bar */}
            <div
                className="relative z-10 border-t border-emerald-900/10"
                style={{ backgroundColor: "rgba(7,77,61,0.06)" }}
            >
                <div className="container mx-auto px-6 max-w-7xl py-4">
                    <p className="text-center text-sm text-gray-500">
                        Copyright © {new Date().getFullYear()}{" "}
                        <a
                            href="#"
                            className="font-semibold transition-colors hover:underline"
                            style={{ color: "#f0a500" }}
                        >
                            <EditableText
                                value={content.companyName || defaultSiteName || "Instacks"}
                                onChange={(v) => onContentChange?.({ ...content, companyName: v })}
                            />
                        </a>{" "}
                        All Rights Reserved
                    </p>
                </div>
            </div>
        </footer>
    );
}

// Default export for backward compatibility
export default EducaeetFooter;
